import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  MapPin,
  Search,
  Clock,
  ChevronRight,
  Navigation,
  Calendar,
  Loader,
  Car,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

const formatDateInput = (date) => date.toISOString().split("T")[0];

const today = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

// ─── Proximity badge ─────────────────────────────────────────────────────────

const ProximityBadge = ({ proximity, isDarkMode }) => {
  const { driveMinutes, straightLineKm } = proximity;
  const minutes = driveMinutes ?? Math.round((straightLineKm ?? 0) * 1.4);

  const colour =
    minutes <= 10
      ? isDarkMode
        ? "text-green-400 bg-green-400/10 border-green-400/30"
        : "text-green-700 bg-green-100 border-green-200"
      : minutes <= 25
        ? isDarkMode
          ? "text-yellow-400 bg-yellow-400/10 border-yellow-400/30"
          : "text-yellow-700 bg-yellow-100 border-yellow-200"
        : isDarkMode
          ? "text-orange-400 bg-orange-400/10 border-orange-400/30"
          : "text-orange-700 bg-orange-100 border-orange-200";

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${colour}`}
    >
      <Car size={11} />
      {proximity.driveTime ?? `~${minutes} min`}
    </span>
  );
};

// ─── Slot pill ────────────────────────────────────────────────────────────────

const SlotPill = ({ slot, onSelect, selected, isDarkMode }) => (
  <button
    onClick={() => onSelect(slot)}
    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
      selected
        ? isDarkMode
          ? "bg-blue-600 border-blue-500 text-white"
          : "bg-blue-500 border-blue-500 text-white"
        : isDarkMode
          ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:border-blue-500 hover:text-white"
          : "bg-white border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-600"
    }`}
  >
    <Clock size={11} />
    {slot.type === "before" ? "Before" : "After"} · {formatDate(slot.startTime)}{" "}
    {formatTime(slot.startTime)}
  </button>
);

// ─── Main Modal ───────────────────────────────────────────────────────────────

const NearbySlotSuggesterModal = ({
  onClose,
  onSlotSelected,
  calendarType = "sales",
}) => {
  const { isDarkMode } = useTheme();
  // Address autocomplete
  const [addressInput, setAddressInput] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const autocompleteTimer = useRef(null);

  // Date range
  const [startDate, setStartDate] = useState(formatDateInput(today()));
  const [endDate, setEndDate] = useState(formatDateInput(addDays(today(), 6)));

  // Results
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  // Selected slot
  const [selectedSlot, setSelectedSlot] = useState(null);

  // ── Autocomplete ────────────────────────────────────────────────────────
  const fetchPredictions = useCallback(async (input) => {
    if (input.trim().length < 3) {
      setPredictions([]);
      return;
    }
    try {
      const res = await apiService.getPlacePredictions(input);
      if (res.success) setPredictions(res.predictions || []);
    } catch {
      /* silent */
    }
  }, []);

  const handleAddressChange = (e) => {
    const val = e.target.value;
    setAddressInput(val);
    setSelectedPlace(null);
    setSuggestions(null);
    setShowDropdown(true);

    clearTimeout(autocompleteTimer.current);
    autocompleteTimer.current = setTimeout(() => fetchPredictions(val), 300);
  };

  const handleSelectPrediction = (prediction) => {
    setSelectedPlace(prediction);
    setAddressInput(prediction.description);
    setPredictions([]);
    setShowDropdown(false);
  };

  // ── Search ───────────────────────────────────────────────────────────────
  const handleSearch = async () => {
    if (!selectedPlace && addressInput.trim().length < 5) {
      setError("Please select an address from the suggestions.");
      return;
    }
    if (!startDate || !endDate) {
      setError("Please choose a date range.");
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setError("End date must be after start date.");
      return;
    }

    setError(null);
    setLoading(true);
    setSuggestions(null);
    setSelectedSlot(null);

    try {
      const payload = {
        startDate,
        endDate,
        maxResults: 6,
        calendarType,
      };

      if (selectedPlace?.placeId) {
        payload.placeId = selectedPlace.placeId;
      } else {
        payload.address = addressInput.trim();
      }

      const res = await apiService.suggestNearbySlots(payload);

      if (res.success) {
        setSuggestions(res.suggestions || []);
        setMeta(res.meta);
      } else {
        setError(res.message || "Could not find nearby appointments.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // ── Confirm ─────────────────────────────────────────────────────────────
  const handleConfirm = () => {
    if (!selectedSlot) return;
    onSlotSelected({
      slot: selectedSlot.slot,
      nearbyAppointment: selectedSlot.appointment,
      targetAddress: selectedPlace?.description || addressInput,
      targetPlaceId: selectedPlace?.placeId || null,
    });
    onClose();
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`border rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh] ${
          isDarkMode
            ? "bg-[#13151A] border-gray-700/60"
            : "bg-white border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b ${
            isDarkMode ? "border-gray-700/60" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-lg ${
                isDarkMode ? "bg-blue-500/15" : "bg-blue-100"
              }`}
            >
              <Navigation
                size={18}
                className={isDarkMode ? "text-blue-400" : "text-blue-600"}
              />
            </div>
            <div>
              <h2
                className={`font-semibold text-base ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Find Nearby Appointment Slots
              </h2>
              <p
                className={`text-xs mt-0.5 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Enter an address to find existing appointments nearby — schedule
                efficiently
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-all ${
              isDarkMode
                ? "text-gray-400 hover:text-white hover:bg-gray-700/50"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            }`}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Address input */}
          <div>
            <label
              className={`block text-sm font-medium mb-1.5 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Target address
            </label>
            <div className="relative">
              <MapPin
                size={15}
                className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              />
              <input
                type="text"
                value={addressInput}
                onChange={handleAddressChange}
                onFocus={() => predictions.length > 0 && setShowDropdown(true)}
                placeholder="Start typing an address…"
                className={`w-full border rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                }`}
              />
              {selectedPlace && (
                <CheckCircle
                  size={15}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500"
                />
              )}

              {/* Autocomplete dropdown */}
              {showDropdown && predictions.length > 0 && (
                <div
                  className={`absolute top-full left-0 right-0 mt-1 rounded-xl shadow-xl z-10 overflow-hidden border ${
                    isDarkMode
                      ? "bg-[#1A1D24] border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  {predictions.map((p) => (
                    <button
                      key={p.placeId}
                      onClick={() => handleSelectPrediction(p)}
                      className={`w-full text-left px-4 py-3 transition-colors flex items-start gap-3 border-b last:border-0 ${
                        isDarkMode
                          ? "hover:bg-gray-700/40 border-gray-700/40"
                          : "hover:bg-gray-50 border-gray-100"
                      }`}
                    >
                      <MapPin
                        size={14}
                        className={`mt-0.5 flex-shrink-0 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                      <div>
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {p.mainText}
                        </div>
                        <div
                          className={`text-xs ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {p.secondaryText}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                className={`block text-sm font-medium mb-1.5 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                From date
              </label>
              <div className="relative">
                <Calendar
                  size={14}
                  className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={`w-full border rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white focus:border-blue-500"
                      : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                  }`}
                />
              </div>
            </div>
            <div>
              <label
                className={`block text-sm font-medium mb-1.5 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                To date
              </label>
              <div className="relative">
                <Calendar
                  size={14}
                  className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                />
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={`w-full border rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white focus:border-blue-500"
                      : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div
              className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm ${
                isDarkMode
                  ? "bg-red-500/10 border border-red-500/30 text-red-400"
                  : "bg-red-50 border border-red-200 text-red-600"
              }`}
            >
              <AlertCircle size={15} className="flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Search button */}
          <button
            onClick={handleSearch}
            disabled={
              loading || (!selectedPlace && addressInput.trim().length < 5)
            }
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
              isDarkMode
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-blue-500 hover:bg-blue-600 text-white"
            }`}
          >
            {loading ? (
              <>
                <Loader size={15} className="animate-spin" />
                Finding nearby appointments…
              </>
            ) : (
              <>
                <Search size={15} />
                Find nearby slots
              </>
            )}
          </button>

          {/* API efficiency note */}
          {meta && (
            <div
              className={`flex items-center gap-2 text-xs ${
                isDarkMode ? "text-gray-500" : "text-gray-400"
              }`}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
              Searched {meta.totalAppointments} appointment
              {meta.totalAppointments !== 1 ? "s" : ""} · {meta.googleApiCalls}{" "}
              Google API call{meta.googleApiCalls !== 1 ? "s" : ""} used
            </div>
          )}

          {/* Results */}
          {suggestions !== null && (
            <>
              {suggestions.length === 0 ? (
                <div
                  className={`text-center py-8 text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  No appointments with location data found near this address in
                  the selected range.
                </div>
              ) : (
                <div className="space-y-3">
                  <h3
                    className={`text-sm font-semibold ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {suggestions.length} nearby appointment
                    {suggestions.length !== 1 ? "s" : ""} found
                  </h3>

                  {suggestions.map((s, idx) => (
                    <div
                      key={s.appointment.id}
                      className={`rounded-xl border p-4 transition-all ${
                        selectedSlot?.appointment.id === s.appointment.id
                          ? isDarkMode
                            ? "border-blue-500/60 bg-blue-500/5"
                            : "border-blue-500 bg-blue-50/50"
                          : isDarkMode
                            ? "border-gray-700/60 bg-[#0C0E12]/60 hover:border-gray-600"
                            : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    >
                      {/* Appointment info row */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={`font-medium text-sm truncate ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {s.appointment.title}
                            </span>
                            <ProximityBadge
                              proximity={s.proximity}
                              isDarkMode={isDarkMode}
                            />
                          </div>
                          <div
                            className={`flex items-center gap-1.5 mt-1 text-xs ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            <Calendar size={11} />
                            {formatDate(s.appointment.startTime)}{" "}
                            {formatTime(s.appointment.startTime)}–
                            {formatTime(s.appointment.endTime)}
                          </div>
                          {s.appointment.location && (
                            <div
                              className={`flex items-center gap-1.5 mt-0.5 text-xs truncate ${
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }`}
                            >
                              <MapPin size={11} />
                              {s.appointment.location}
                            </div>
                          )}
                          {s.appointment.assignee && (
                            <div
                              className={`flex items-center gap-1.5 mt-0.5 text-xs ${
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }`}
                            >
                              <span
                                className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                                  isDarkMode
                                    ? "bg-gray-600 text-white"
                                    : "bg-gray-300 text-gray-700"
                                }`}
                              >
                                {s.appointment.assignee.name?.[0] ?? "?"}
                              </span>
                              {s.appointment.assignee.name}
                            </div>
                          )}
                        </div>

                        {s.proximity.driveDistance && (
                          <div className="text-right flex-shrink-0">
                            <div
                              className={`text-xs ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {s.proximity.driveDistance}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Suggested slots */}
                      <div className="flex flex-wrap gap-2">
                        {s.suggestedSlots.map((slot) => (
                          <SlotPill
                            key={slot.type}
                            slot={slot}
                            selected={
                              selectedSlot?.slot?.type === slot.type &&
                              selectedSlot?.appointment.id === s.appointment.id
                            }
                            onSelect={(sl) =>
                              setSelectedSlot({
                                slot: sl,
                                appointment: s.appointment,
                              })
                            }
                            isDarkMode={isDarkMode}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex items-center justify-between gap-3 ${
            isDarkMode ? "border-gray-700/60" : "border-gray-200"
          }`}
        >
          <div
            className={`text-xs ${
              isDarkMode ? "text-gray-500" : "text-gray-400"
            }`}
          >
            {selectedSlot
              ? `Selected: ${formatDate(selectedSlot.slot.startTime)} ${formatTime(selectedSlot.slot.startTime)} (${selectedSlot.slot.type} nearby appointment)`
              : "Select a time slot above to use it"}
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isDarkMode
                  ? "bg-[#0C0E12] border border-gray-700 text-gray-300 hover:text-white hover:border-gray-600"
                  : "bg-white border border-gray-300 text-gray-700 hover:text-gray-900 hover:border-gray-400"
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedSlot}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                isDarkMode
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            >
              Use this slot
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NearbySlotSuggesterModal;
